<template>
  <b-dropdown
    text="Recent History"
    variant="outline-secondary"
    size="sm"
    @show="onOpen"
  >
    <div class="recent-feed-history-list">
      <div
        v-if="loading"
        class="dropdown-item-text text-muted"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="dropdown-item-text text-danger"
      >
        {{ error }}
      </div>
      <div
        v-else-if="histories.length === 0"
        class="dropdown-item-text text-muted"
      >
        No recent imports found.
      </div>
      <template v-else>
        <div
          v-for="history in histories"
          :key="rowKey(history)"
          class="dropdown-item-text recent-feed-history-row"
        >
          <div>
            {{ formatDate(history.created_at) }} {{ formatTime(history.time) }}
            <span
              class="badge"
              :class="history.source === 'not_imported' ? 'badge-danger' : 'badge-success'"
            >
              {{ history.source === 'not_imported' ? 'Not Imported' : 'Imported' }}
            </span>
          </div>
          <div>
            <a
              v-if="history.layout"
              :href="`/banner/${history.layout.customer_id}/group/${history.layout_id}/show`"
              target="_blank"
            >
              Layout {{ history.layout_id }}
            </a>
            <span v-else>Layout {{ history.layout_id }} (deleted)</span>
            <span v-if="history.type">&nbsp;&middot; {{ history.type }}</span>
          </div>
          <div
            v-if="isDownloadableHistory(history)"
            class="dropdown recent-feed-history-actions"
          >
            <a
              href="#"
              role="button"
              data-toggle="dropdown"
              class="action"
              @click.prevent
            >
              <b-icon
                icon="three-dots-vertical"
                class="text-body font-2xl"
              />
            </a>
            <div class="dropdown-menu dropdown-menu-right">
              <button
                type="button"
                class="dropdown-item"
                @click="downloadFile(history.file_path)"
              >
                <i class="bi bi-cloud-arrow-down" />&nbsp;Download
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </b-dropdown>
</template>

<script>
export default {
  name: "RecentFeedHistoryDropdown",
  props: {
    layoutId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loaded: false,
      loading: false,
      error: null,
      histories: [],
    };
  },
  mounted() {
    // #ImportXlsModal is hidden/shown in place (never remounted), so this
    // component's `loaded` cache would otherwise persist for the whole page
    // session. Reset it on modal close so each modal open can fetch fresh
    // data, matching how the rest of the modal treats `hidden.bs.modal`
    // (see resources/js/frontend/group/edit.js).
    $('#ImportXlsModal').on('hidden.bs.modal.recentFeedHistoryDropdown', () => {
      this.loaded = false;
    });
  },
  beforeDestroy() {
    $('#ImportXlsModal').off('hidden.bs.modal.recentFeedHistoryDropdown');
  },
  methods: {
    onOpen() {
      if (this.loaded || this.loading) {
        return;
      }

      this.fetchRecent();
    },
    async fetchRecent() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/import-history/fetch-recent", {
          params: { layout_id: this.layoutId },
        });

        if (response.data.status === "success") {
          this.histories = response.data.data;
          this.loaded = true;
        } else {
          this.error = response.data.message || "Failed to load recent history.";
        }
      } catch (error) {
        console.log("Error fetching recent history:", error);
        this.error = "Failed to load recent history.";
      } finally {
        this.loading = false;
      }
    },
    rowKey(history) {
      return history.source === "not_imported"
        ? `not_imported_${history.source_id}_${history.layout_id}`
        : `imported_${history.source_id}`;
    },
    isDownloadableHistory(history) {
      return history.source === "imported" && Boolean(history.file_path);
    },
    async downloadFile(filePath) {
      try {
        const { data } = await axios.get("/import-history/download-file", {
          params: { file_path: filePath },
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filePath.split("/").pop());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log("Error downloading file:", error);
        toastr.error("Failed to download the file.");
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
    },
    formatTime(timeString) {
      return new Date(`2000-01-01 ${timeString}`).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
};
</script>

<style scoped>
.recent-feed-history-list {
  max-height: 260px;
  overflow-y: auto;
  min-width: 280px;
}

.recent-feed-history-row {
  position: relative;
  border-bottom: 1px solid #e5e7eb;
  padding-right: 2rem;
}

.recent-feed-history-row:last-child {
  border-bottom: none;
}

.recent-feed-history-actions {
  position: absolute;
  top: 0.35rem;
  right: 0.5rem;
}
</style>
